import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import CustomSelect from "components/CustomSelect";
import CustomTextFiled from "components/CustomTextField";
import { ROUTES } from "constants/routes";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import tractorService from "services/tractor.service";
import { fetchAllManufacturerModalsAction } from "slices/generalReducer";
import { useAppDispatch } from "store/appHooks";
import { getAllYears } from "utils/years.util";
import { Label, SubTitle, Title, TypeButton } from "../mui.styles";

const BasicDetails = (props: any) => {
  const {
    currentStep,
    generalReducer,
    setCurrentStep,
    changeStepStatus,
    editId,
    stepStatus,
  } = props;

  const {
    setValue,
    control,
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const values = watch();
  const { manufacturer, attachments, models } = generalReducer;
  const { data: manufacturerData, loading: manufacturerLoading } = manufacturer;
  const { data: attachmentsData, loading: attachmentsLoading } = attachments;
  const { data: modelsData, loading: modelsLoading } = models;
  const [yearOpts, setYearOpts] = useState([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const allYrs: any = getAllYears();
    setYearOpts(allYrs?.map((el: any) => ({ id: el, name: el })));
    setValue("capacity_unit", "FTS");
  }, []);

  useEffect(() => {
    let timeOutId = setTimeout(
      () => !editId && initDraft(values?.equipmentType),
      500
    );
    return () => {
      clearTimeout(timeOutId);
    };
  }, []);

  useEffect(() => {
    clearErrors([
      "listing_name",
      "listing_description",
      "emv",
      "manufacturer_id",
      "manufacturer_model_id",
      "horse_power",
      "year",
      "attachment_id",
      "attachment_type",
      "capacity",
      "capacity_unit",
    ]);
    // setValue("manufacturer_id", undefined);
    // setValue("attachment_id", undefined);
  }, [values?.equipmentType]);

  useEffect(() => {
    values?.manufacturer_id &&
      dispatch(
        fetchAllManufacturerModalsAction({ id: values?.manufacturer_id })
      );
  }, [values?.manufacturer_id]);

  useEffect(() => {
    if (stepStatus[currentStep]?.error) {
      trigger(getReqFields());
    }
  }, [stepStatus[currentStep]?.error]);

  const getReqFields = () => {
    let reqFields = ["listing_name", "listing_description", "emv"];
    if (values?.equipmentType == 1) {
      reqFields = reqFields.concat([
        "manufacturer_id",
        "manufacturer_model_id",
        "horse_power",
        "year",
      ]);
    } else {
      reqFields = reqFields.concat([
        "attachment_id",
        "attachment_type",
        "capacity",
        "capacity_unit",
      ]);
    }
    return reqFields;
  };

  const navigateNext = async () => {
    const res = await trigger(getReqFields());

    if (res) {
      setCurrentStep(
        values?.equipmentType == 1 ? "Configurations" : "Listing Images"
      );
      changeStepStatus(currentStep, "submitted", true);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const initDraft = async (typ: any) => {
    if (!editId) {
      let tractorDraftId: any = await tractorService.createDraft({
        body: { type: typ },
      });
      const data = tractorDraftId?.data?.data;
      const userProfile: any = data?.user?.user_profile_configs[0];
      if (userProfile?.security_deposit == 0) {
        return navigate(ROUTES.APPROVAL_POLICY + "?backTo=/list-equipment");
      }
      tractorDraftId = data.id ?? 0;
      setValue("id", tractorDraftId);
    }
  };

  return (
    <Box sx={{ p: "10px 16px" }}>
      <Title>Tell us about your Equipment</Title>
      <SubTitle>We require essential information about your Equipment</SubTitle>
      <br />
      <Label>Equipment Type</Label>
      <Box>
        <TypeButton
          color="primary"
          variant={values?.equipmentType == 1 ? "contained" : "outlined"}
          onClick={() => !editId && setValue("equipmentType", 1)}
          sx={{
            ...(editId && {
              cursor: values?.equipmentType == 1 ? "pointer" : "not-allowed",
            }),
          }}
          startIcon={
            <img
              alt="tractot"
              src={require(`assets/img/png/${"tractor_ic.png"}`)}
            />
          }
        >
          Tractor
        </TypeButton>
        <TypeButton
          color="primary"
          variant={values?.equipmentType == 2 ? "contained" : "outlined"}
          onClick={() => !editId && setValue("equipmentType", 2)}
          sx={{
            ...(editId && {
              cursor: values?.equipmentType == 2 ? "pointer" : "not-allowed",
            }),
          }}
          startIcon={
            <img
              alt="attachment"
              src={require(`assets/img/png/${"attachment_ic.png"}`)}
            />
          }
        >
          Attachment
        </TypeButton>
      </Box>
      <br />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={12}>
          {/* <Box ></Box> */}
          <Controller
            control={control}
            name={"listing_name"}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextFiled
                showEndDropDown={false}
                inputValue={value}
                label="Enter listing name"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                // inputProps={{ style: { border: "1px solid red" } }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={"listing_description"}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextFiled
                showEndDropDown={false}
                inputValue={value}
                label="Enter listing description"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                multiline={true}
                rows={3}
                inputHeight="100%"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          {manufacturerLoading || attachmentsLoading ? (
            <Skeleton variant="rectangular" width="100%" height={40} />
          ) : (
            <Controller
              control={control}
              name={
                values?.equipmentType == 1 ? "manufacturer_id" : "attachment_id"
              }
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  label={
                    values?.equipmentType == 1
                      ? "Choose Manufacturer"
                      : "Choose attachment category"
                  }
                  error={error}
                  value={value}
                  onChange={onChange}
                  options={
                    values?.equipmentType == 1
                      ? manufacturerData
                      : attachmentsData
                  }
                  showHelperText={error}
                  helperText={error?.message || "Please select one option"}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={6}>
          {(modelsLoading || manufacturerLoading) &&
          values?.equipmentType == 1 ? (
            <Skeleton variant="rectangular" width="100%" height={40} />
          ) : (
            <Controller
              control={control}
              name={
                values?.equipmentType == 1
                  ? "manufacturer_model_id"
                  : "attachment_type"
              }
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  label={
                    values?.equipmentType == 1 ? "Choose Model" : "Choose type"
                  }
                  error={!!error}
                  value={value}
                  onChange={onChange}
                  options={
                    values?.equipmentType == 1
                      ? modelsData
                      : [
                          { id: "3PT Drag", name: "3PT Drag" },
                          { id: "3PT PTO", name: "3PT PTO" },
                        ]
                  }
                  showHelperText={!!error}
                  helperText={error?.message || "Please select one option"}
                />
              )}
            />
          )}
        </Grid>

        {values?.equipmentType == 1 && (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Controller
              control={control}
              name={"horse_power"}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextFiled
                  showEndDropDown={false}
                  inputValue={value}
                  label="Horse Power(HP)"
                  onInputChange={onChange}
                  showError={!!error}
                  errorMessage={error?.message || " "}
                  inputProps={{ type: "number" }}
                />
              )}
            />
          </Grid>
        )}

        {values?.equipmentType == 1 && (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Controller
              control={control}
              name={"year"}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  value={value}
                  label="Choose year"
                  onChange={onChange}
                  error={!!error}
                  options={yearOpts}
                  showHelperText={!!error}
                  helperText={error?.message || `please select an option!`}
                />
              )}
            />
          </Grid>
        )}

        {values?.equipmentType == 2 && (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Controller
              control={control}
              name={"capacity"}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextFiled
                  inputValue={value}
                  label="Enter capacity"
                  onInputChange={onChange}
                  showError={!!error}
                  errorMessage={error?.message || " "}
                  inputProps={{ type: "number" }}
                  showEndDropDown={true}
                  dropDownValue={values?.capacity_unit}
                  dropDownOptions={[
                    { id: "FTS", name: "FTS" },
                    { id: "LBS", name: "LBS" },
                    { id: "GAL", name: "GAL" },
                    { id: "YDS", name: "YDS" },
                  ]}
                  onDropDownChange={(v: any) => setValue("capacity_unit", v)}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Controller
            control={control}
            name={"emv"}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextFiled
                showEndDropDown={false}
                inputValue={value}
                label="Enter Estimated Market Value(EMV)$"
                onInputChange={onChange}
                showError={!!error}
                errorMessage={error?.message || " "}
                inputProps={{ type: "number" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "22px",
              color: "#323232",
            }}
          >
            You can explain your Equipment, attachment and all highlights here
            that can help you rent your equipment. You can also highlight your
            farming knowledge and expertise to guide and help other farmers and
            renters.
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="end">
            <Button variant="contained" onClick={navigateNext}>
              Continue
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicDetails;
